import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
  static targets = [
    "pinWrap", "header", "title", "subtitle",
    "content", "mobileContent", "mobileStep",
    "step", "panel"
  ]

  connect() {
    document.fonts.ready.then(() => {
      requestAnimationFrame(() => this.setup())
    })
  }

  disconnect() {
    ScrollTrigger.getAll().forEach((t) => t.kill())
  }

  setup() {
    this.isMobile = window.innerWidth < 640
    const pinWrap = this.pinWrapTarget
    const title = this.titleTarget
    const subtitle = this.subtitleTarget
    if (!pinWrap || !title || !subtitle) return

    const panels = this.panelTargets
    const count = 4
    const totalScroll = this.isMobile ? 2000 : 3600
    const introEnd = 0.15

    this.activeIndex = -1
    this.revealed = new Set()

    // Hide all panels initially
    panels.forEach((panel) => {
      gsap.set(panel, { autoAlpha: 0 })
    })

    // Hide desktop steps only (not mobile ones)
    if (this.hasContentTarget) {
      this.contentTarget
        .querySelectorAll(
          "[data-how-it-works-target='step']"
        )
        .forEach((step) => {
          step.style.opacity = "0"
        })
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: pinWrap,
        start: "top top",
        end: `+=${totalScroll}`,
        pin: true,
        scrub: true,
        pinSpacing: true,
        onUpdate: (self) => {
          const p = self.progress
          if (p <= introEnd) {
            this.activateStep(-1, panels)
            return
          }
          const stepP =
            (p - introEnd) / (1 - introEnd)
          const idx = Math.min(
            Math.floor(stepP * count),
            count - 1
          )
          this.activateStep(idx, panels)
        }
      }
    })

    // Phase 1: Appreciation pause
    tl.to({}, { duration: 0.05 })

    // Phase 2: Shrink title + subtitle
    tl.to(title, {
      fontSize: this.isMobile ?
        "1.5rem" : "2.25rem",
      marginBottom: "0.25rem",
      duration: 0.1,
      ease: "power2.inOut"
    })

    tl.to(subtitle, {
      fontSize: this.isMobile ?
        "1rem" : "1.25rem",
      duration: 0.08,
      ease: "power2.inOut"
    }, "<")

    tl.to(this.headerTarget, {
      marginBottom: this.isMobile ?
        "2rem" : "2.5rem",
      duration: 0.1,
      ease: "power2.inOut"
    }, "<")

    tl.to(pinWrap.firstElementChild, {
      paddingTop: this.isMobile ?
        "1rem" : "1.5rem",
      duration: 0.1,
      ease: "power2.inOut"
    }, "<")

    // Phase 3: Scroll distance for step cycling
    tl.to({}, { duration: 0.78 })
  }

  activateStep(index, panels) {
    if (this.activeIndex === index) return
    this.activeIndex = index

    if (this.isMobile) {
      this.activateMobile(index, panels)
    } else {
      this.activateDesktop(index, panels)
    }
  }

  activateMobile(index, panels) {
    const wrapper = this.hasMobileContentTarget ?
      this.mobileContentTarget : null
    const mobileSteps = this.mobileStepTargets

    // Show/hide wrapper
    if (wrapper) {
      if (index >= 0) {
        gsap.to(wrapper, {
          opacity: 1,
          duration: 0.3,
          ease: "power1.out"
        })
      } else {
        gsap.set(wrapper, { opacity: 0 })
      }
    }

    // Swap mobile steps
    mobileSteps.forEach((ms, i) => {
      if (i === index) {
        gsap.to(ms, {
          opacity: 1,
          duration: 0.3,
          ease: "power1.out"
        })
      } else {
        gsap.to(ms, {
          opacity: 0,
          duration: 0.2,
          ease: "power1.in"
        })
      }
    })

    // Swap panels with delay (same as desktop)
    if (this.panelDelay) this.panelDelay.kill()

    panels.forEach((panel) => {
      gsap.set(panel, { autoAlpha: 0 })
    })

    if (index >= 0 && panels[index]) {
      this.panelDelay = gsap.delayedCall(
        0.35,
        () => {
          gsap.to(panels[index], {
            autoAlpha: 1,
            duration: 0.3,
            ease: "power1.out"
          })
        }
      )
    }
  }

  activateDesktop(index, panels) {
    const steps = this.stepTargets
    const wrapper = this.hasContentTarget ?
      this.contentTarget : null

    // Show/hide wrapper
    if (wrapper) {
      if (index >= 0) {
        gsap.to(wrapper, {
          opacity: 1,
          duration: 0.3,
          ease: "power1.out"
        })
      } else {
        gsap.set(wrapper, { opacity: 0 })
      }
    }

    // Reveal steps progressively
    steps.forEach((step, i) => {
      if (i <= index) {
        if (!this.revealed.has(i)) {
          this.revealed.add(i)
          gsap.to(step, {
            opacity: 1,
            duration: 0.3,
            ease: "power1.out"
          })
        }
      } else {
        this.revealed.delete(i)
        gsap.set(step, { opacity: 0 })
      }
    })

    // Style active/inactive steps
    steps.forEach((step, i) => {
      const circle = step.querySelector(
        "[data-role='circle']"
      )
      const desc = step.querySelector(
        "[data-role='desc']"
      )
      if (i === index) {
        step.classList.add("bg-white", "text-black")
        step.classList.remove(
          "bg-gray-900", "text-white",
          "hover:bg-gray-800"
        )
        if (circle) {
          circle.classList.add(
            "bg-black", "text-white"
          )
          circle.classList.remove(
            "bg-white", "text-black"
          )
        }
        if (desc) {
          desc.classList.replace(
            "text-gray-400", "text-gray-500"
          )
        }
      } else {
        step.classList.remove(
          "bg-white", "text-black"
        )
        step.classList.add(
          "bg-gray-900", "text-white",
          "hover:bg-gray-800"
        )
        if (circle) {
          circle.classList.remove(
            "bg-black", "text-white"
          )
          circle.classList.add(
            "bg-white", "text-black"
          )
        }
        if (desc) {
          desc.classList.replace(
            "text-gray-500", "text-gray-400"
          )
        }
      }
    })

    // Swap panels with delay
    if (this.panelDelay) this.panelDelay.kill()

    panels.forEach((panel) => {
      gsap.set(panel, { autoAlpha: 0 })
    })

    if (index >= 0 && panels[index]) {
      this.panelDelay = gsap.delayedCall(
        0.35,
        () => {
          gsap.to(panels[index], {
            autoAlpha: 1,
            duration: 0.3,
            ease: "power1.out"
          })
        }
      )
    }
  }
}
